'use client';

import clsx from 'classnames';
import { Children } from 'react';
import SwiperCore, {
  Autoplay,
  Navigation,
  Pagination,
  Scrollbar,
  SwiperOptions,
  Swiper as SwiperProps,
} from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './Slider.module.scss';
import { SliderProps } from './types';

SwiperCore.use([Navigation]);

export const Slider = ({
  allowSwipe = true,
  breakpoints,
  bulletOptions = [],
  centeredSlides = false,
  children,
  hasPagination = false,
  hasScrollbar = false,
  hasArrows = false,
  initialSlide = 0,
  loop = false,
  preventClicks = true,
  preventClicksPropagation = true,
  slidesPerView = 1,
  slidesPerGroup = 1,
  slidesPerGroupAuto = false,
  slideToClickedSlide = false,
  spaceBetween = 50,
  speed = 300,
  swiperClassName,
  sliderName,
  watchOverflow = false,
  autoplay = false,
  onSlideChange,
}: SliderProps) => {
  return (
    <div className={clsx(`Slider`, styles.slider, sliderName)}>
      <Swiper
        autoplay={autoplay}
        allowTouchMove={allowSwipe}
        breakpoints={breakpoints}
        centeredSlides={centeredSlides}
        modules={[Pagination, Autoplay, Scrollbar]}
        scrollbar={{
          hide: true,
        }}
        className={clsx(
          styles.slider__swiper,
          {
            [styles['slider__swiper--pagination']]:
              !!hasPagination &&
              (slidesPerView === 'auto'
                ? true
                : Array.isArray(children)
                  ? children.length > slidesPerView
                  : false),
          },
          {
            [styles['slider__swiper--scrollbar']]:
              !!hasScrollbar &&
              (slidesPerView === 'auto'
                ? true
                : Array.isArray(children)
                  ? children.length > slidesPerView
                  : false),
          },
          swiperClassName
        )}
        loop={loop}
        initialSlide={initialSlide}
        navigation={
          hasArrows
            ? {
                prevEl: '.swiperPrevArrow',
                nextEl: '.swiperNextArrow',
              }
            : false
        }
        preloadImages={false}
        pagination={{
          clickable: true,
          renderBullet(index, className) {
            return `<div class=${className}><span>${
              bulletOptions.length > 0 ? bulletOptions[index] : ''
            }</span></div>`;
          },
        }}
        slidesPerView={slidesPerView}
        slidesPerGroup={slidesPerGroup}
        slidesPerGroupAuto={slidesPerGroupAuto}
        slideToClickedSlide={slideToClickedSlide}
        spaceBetween={spaceBetween}
        speed={speed}
        preventClicks={preventClicks}
        preventClicksPropagation={preventClicksPropagation}
        watchOverflow={watchOverflow}
        on={{
          init(swiper: SwiperProps): void {
            swiper.navigation?.destroy();
            const breakpoint: string = swiper.getBreakpoint(
              swiper.params.breakpoints
            );
            const currentBreakpoint: SwiperOptions | undefined =
              swiper.params.breakpoints?.[breakpoint];
            const { navigation: currentBreakpointNavConfig } =
              currentBreakpoint || {};

            if (!currentBreakpointNavConfig) {
              return;
            }

            if (
              currentBreakpointNavConfig === true ||
              currentBreakpointNavConfig?.enabled
            ) {
              swiper.navigation.init();
              swiper.navigation.update();
            }
          },
        }}
        onSlideChange={onSlideChange}
      >
        {hasArrows && (
          <div
            className={clsx('swiperPrevArrow', styles['slidePrev-button'])}
          />
        )}
        {Array.isArray(children) ? (
          Children.toArray(children).map((slide, index) => (
            <SwiperSlide key={index}>{slide}</SwiperSlide>
          ))
        ) : (
          <SwiperSlide>{children}</SwiperSlide>
        )}
        {hasArrows && (
          <div
            className={clsx('swiperNextArrow', styles['slideNext-button'])}
          />
        )}
      </Swiper>
    </div>
  );
};
